import { Link, useParams } from 'react-router-dom'
import {
  Briefcase,
  Building2,
  ExternalLink,
  History,
  Lightbulb,
  Mail,
} from 'lucide-react'
import {
  combineQueryStates,
  LinkChip,
  NotFoundView,
  PageHeader,
  PersonAvatar,
  QueryErrorPanel,
  safeHttpUrl,
  SectionHeader,
  Tag,
} from '@loop3/ui'
import { AskLupeButton } from '@/components/ask-lupe-button'
import { DetailSkeleton } from '@/components/explorer-detail'
import { EntityGrid } from '@/components/entity-card'
import { PersonProfileSection } from '@/components/explorer/profile-section'
import { usePerson } from '@/hooks/usePerson'
import { usePersonSolutions } from '@/hooks/usePersonSolutions'
import { isUuid } from '@/lib/compute/universe'
import type { Entity, PersonDetail } from '@/lib/types/universe'

function companyCards(affs: PersonDetail['affiliations']): Entity[] {
  return affs.map((a) => ({
    kind: 'company',
    id: a.company.slug,
    title: a.company.name,
    subtitle: a.role ?? undefined,
  }))
}

function PersonNotFound() {
  return (
    <NotFoundView
      title="Person not found"
      description="No one in the universe has this id."
      homeLink={<Link to="/explorer" />}
      homeLabel="Back to Explorer"
    />
  )
}

/** One person: contact links, Loop3 team membership, companies and client-contact roles. */
export function PersonPage() {
  const { id } = useParams<{ id: string }>()
  const valid = !!id && isUuid(id)
  const person = usePerson(id)
  const worked = usePersonSolutions(id)
  const { status, retry } = combineQueryStates([person])

  if (!valid) return <PersonNotFound />
  if (status === 'error')
    return (
      <div className="mx-auto max-w-7xl pb-16">
        <QueryErrorPanel onRetry={retry} />
      </div>
    )
  if (status === 'pending') return <DetailSkeleton />
  if (!person.data) return <PersonNotFound />

  const p = person.data
  const current = p.affiliations.filter((a) => a.current)
  const previous = p.affiliations.filter((a) => !a.current)
  const linkedin = safeHttpUrl(p.linkedin_url)
  const nothingKnown =
    !p.teamMember && p.affiliations.length === 0 && p.contactOf.length === 0

  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        className="mt-2"
        eyebrow={p.teamMember ? 'Loop3 team' : 'Person'}
        title={
          <span className="flex items-center gap-3">
            <PersonAvatar label={p.full_name} colorSeed={p.id} size={14} />
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {p.full_name}
            </span>
          </span>
        }
        actions={
          <AskLupeButton
            question={`What has ${p.full_name} been involved in?`}
          />
        }
      >
        {(p.email || linkedin) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {p.email && (
              <LinkChip
                href={`mailto:${p.email}`}
                icon={
                  <Mail className="size-3.5 shrink-0 opacity-60" aria-hidden />
                }
                label={p.email}
                className="min-w-0 [overflow-wrap:anywhere]"
              />
            )}
            {linkedin && (
              <LinkChip
                href={linkedin}
                icon={
                  <ExternalLink
                    className="size-3.5 shrink-0 opacity-60"
                    aria-hidden
                  />
                }
                label="LinkedIn"
              />
            )}
          </div>
        )}
      </PageHeader>

      {p.teamMember && (
        <section className="mb-8 rounded-xl border border-[var(--l3-violet)]/30 bg-[var(--l3-violet-soft)] p-4">
          <span className="eyebrow text-[var(--l3-violet)]">Loop3 team</span>
          <div className="mt-2 text-sm font-medium">
            {p.teamMember.role ?? p.teamMember.name}
          </div>
          {p.teamMember.teams.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {p.teamMember.teams.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </div>
          )}
        </section>
      )}

      <PersonProfileSection profile={p.profile} />

      {worked.length > 0 && (
        <section className="mb-8">
          <SectionHeader
            icon={Lightbulb}
            label="Worked on"
            meta={worked.length}
          />
          <EntityGrid
            items={worked.map((s) => ({
              kind: 'solution',
              id: s.id,
              title: s.name,
              subtitle: s.client?.name,
            }))}
          />
        </section>
      )}

      {current.length > 0 && (
        <section className="mb-8">
          <SectionHeader
            icon={Briefcase}
            label="Works at"
            meta={current.length}
          />
          <EntityGrid items={companyCards(current)} />
        </section>
      )}

      {previous.length > 0 && (
        <section className="mb-8 opacity-80">
          <SectionHeader
            icon={History}
            label="Previously"
            meta={previous.length}
          />
          <EntityGrid items={companyCards(previous)} />
        </section>
      )}

      {p.contactOf.length > 0 && (
        <section className="mb-8">
          <SectionHeader
            icon={Building2}
            label="Contact at"
            meta={p.contactOf.length}
          />
          <EntityGrid
            items={p.contactOf.map((c) => ({
              kind: 'client',
              id: c.prefix,
              title: c.name,
              subtitle: c.role ?? c.prefix,
            }))}
          />
        </section>
      )}

      {nothingKnown && (
        <p className="py-6 text-center text-sm opacity-60">
          No companies or client roles on record yet. Lupe may know more.
        </p>
      )}
    </div>
  )
}
